import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Users, MessageCircle, Calendar, UserPlus, LogOut } from 'lucide-react';
import api from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { RecurringBadge } from '../components/RecurrencePicker';
import CommunityRecurringPrompt from '../components/CommunityRecurringPrompt';

export default function CommunityProfile() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [c, setC] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [showRecurring, setShowRecurring] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/communities/${id}`);
      setC(data);
    } catch { navigate('/communities'); }
    setLoading(false);
  }, [id, navigate]);

  useEffect(() => { load(); }, [load]);

  const join = async () => {
    setBusy(true);
    try {
      await api.post(`/communities/${id}/join`);
      await load();
      // Q32 Phase B — if this community has any recurring events, prompt
      // the user to bulk-save the next 4 to their calendar.
      try {
        const { data } = await api.get(`/calendar/community/${id}/upcoming?limit=4`);
        if (data?.has_recurring) setShowRecurring(true);
      } catch { /* silent — non-blocking enrichment */ }
    } finally {
      setBusy(false);
    }
  };
  const leave = async () => {
    setBusy(true);
    try { await api.post(`/communities/${id}/leave`); await load(); } finally { setBusy(false); }
  };
  const openChat = () => {
    if (c?.group_chat_id) navigate(`/groups/${c.group_chat_id}`);
  };

  if (loading) return <div className="content-area px-4 pt-12 text-center text-[#9B9EA3]" data-testid="community-loading">Loading…</div>;
  if (!c) return null;

  return (
    <div className="content-area pb-12" data-testid="community-profile-page">
      <div className="px-4 pt-5 pb-3 flex items-center gap-3">
        <button data-testid="community-back" onClick={() => navigate(-1)} aria-label="Back">
          <ArrowLeft size={20} className="text-[#111]" />
        </button>
        <h1 className="text-lg font-bold tracking-tight text-[#111111] truncate flex-1" data-testid="community-name-header">
          {c.name}
        </h1>
      </div>

      <div className="px-4 space-y-4">
        <div className="bg-white rounded-3xl border border-black/[0.06] p-5">
          <div className="flex items-start gap-3">
            {c.cover_image ? (
              <img src={c.cover_image} alt="" className="w-14 h-14 rounded-2xl object-cover flex-shrink-0" />
            ) : (
              <div className="w-14 h-14 rounded-2xl bg-[#B6E600]/15 text-[#3F5200] flex items-center justify-center flex-shrink-0">
                <Users size={22} />
              </div>
            )}
            <div className="flex-1 min-w-0">
              <h2 data-testid="community-name" className="text-base font-extrabold text-[#111]">{c.name}</h2>
              <p className="text-xs text-[#6B6E73] mt-0.5">
                <span data-testid="community-member-count">{c.member_count}</span> members · {c.city || 'Anywhere'}
                {c.category && <> · <span className="capitalize">{c.category}</span></>}
              </p>
              {c.is_host && (
                <span className="inline-block mt-2 px-2 py-0.5 rounded-full bg-[#B6E600]/20 text-[#3F5200] text-[10px] font-bold uppercase tracking-wider">
                  You host this
                </span>
              )}
            </div>
          </div>

          {c.description && <p className="mt-4 text-sm text-[#2A2A2A] leading-relaxed">{c.description}</p>}

          <div className="mt-4 flex gap-2">
            {c.is_member ? (
              <>
                <button
                  data-testid="community-chat-btn"
                  onClick={openChat}
                  className="flex-1 bg-[#B6E600] text-[#111] font-bold rounded-full py-2.5 text-sm flex items-center justify-center gap-2 active:scale-[0.98]"
                >
                  <MessageCircle size={14} /> Open chat
                </button>
                {!c.is_host && (
                  <button
                    data-testid="community-leave-btn"
                    onClick={leave}
                    disabled={busy}
                    className="px-4 bg-white border border-black/[0.08] text-[#FF3B30] font-bold rounded-full py-2.5 text-sm flex items-center justify-center gap-1.5 disabled:opacity-50"
                  >
                    <LogOut size={14} /> Leave
                  </button>
                )}
              </>
            ) : (
              <button
                data-testid="community-join-btn"
                onClick={join}
                disabled={busy || !user}
                className="flex-1 bg-[#B6E600] text-[#111] font-bold rounded-full py-2.5 text-sm flex items-center justify-center gap-2 disabled:opacity-50"
              >
                <UserPlus size={14} /> {busy ? 'Joining…' : 'Join community'}
              </button>
            )}
          </div>
        </div>

        {/* Upcoming events */}
        <section data-testid="community-events">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-extrabold text-[#111111] uppercase tracking-wider">Upcoming events</h3>
            {c.is_host && (
              <button
                data-testid="community-add-event"
                onClick={() => navigate(`/create?community_id=${c.id}`)}
                className="text-[11px] font-bold text-[#3F5200] underline-offset-2 underline"
              >
                + New event
              </button>
            )}
          </div>

          {(c.upcoming_events || []).length === 0 ? (
            <div className="bg-white rounded-2xl border border-black/[0.06] p-5 text-center">
              <Calendar size={20} className="mx-auto text-[#B0B3B8] mb-2" />
              <p className="text-sm text-[#6B6E73]">No events scheduled</p>
              {c.is_host && <p className="text-xs text-[#9B9EA3] mt-1">Host the next one!</p>}
            </div>
          ) : (
            <div className="space-y-2">
              {c.upcoming_events.map(e => (
                <button
                  key={e.id}
                  data-testid={`community-event-${e.id}`}
                  onClick={() => navigate(`/plan/${e.id}`)}
                  className="w-full flex items-center gap-3 bg-white rounded-2xl border border-black/[0.06] p-3 active:bg-[#F1F0EA]"
                >
                  <div className="w-10 h-10 rounded-xl bg-[#F1F0EA] text-[#111] flex items-center justify-center text-xs font-bold">
                    {(e.date || '').slice(5).replace('-', '/')}
                  </div>
                  <div className="flex-1 text-left min-w-0">
                    <div className="flex items-center gap-1.5">
                      <p className="text-sm font-bold text-[#111] truncate flex-1">{e.title}</p>
                      <RecurringBadge plan={e} compact />
                    </div>
                    <p className="text-[11px] text-[#6B6E73] truncate">{e.start_time} · {e.venue}</p>
                  </div>
                </button>
              ))}
            </div>
          )}
        </section>
      </div>
      <CommunityRecurringPrompt
        open={showRecurring}
        communityId={id}
        onClose={() => setShowRecurring(false)}
      />
    </div>
  );
}
